import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z, ZodSchema } from "zod";
import { GoogleAuthProvider, OAuthProvider, signOut } from "firebase/auth";
import { useMutation } from "@tanstack/react-query";
import { formatFirebaseError } from "@shared/services/api/formatFirebaseError";
import { registerUserWithOAuth } from "../utils/registerUserWithOAuth";
import { authEventBus } from "../auth.events";
import { Invitation } from "@platform/types/platform.types";
import { initFirebase } from "@shared/services/api/firebase";
import { AppType } from "@shared/types/app.types";
import { getConfig } from "@shared/services/api/config";

type Args = {
  invitation: Invitation;
  onError?: () => void;
  onSuccess?: (data: any) => void;
  schema: ZodSchema;
  form: UseFormReturn<z.infer<typeof schema>>;
};

export function useGoogleSignupMutation({
  invitation,
  onError,
  onSuccess,
  schema,
  form,
}: Args) {
  const { appName } = getConfig();

  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (data: z.infer<typeof schema>) => {
      return await registerUserWithOAuth(
        appName as AppType,
        new GoogleAuthProvider() as OAuthProvider,
        invitation?.code,
        onError
      );
    },
    onError: async (error: Error) => {
      form.setError("root", {
        type: "manual",
        message: formatFirebaseError(error.message),
      });

      if (error.message.indexOf("Email address already in use.") > -1) {
        const auth = await initFirebase();

        await signOut(auth);
      }

      if (
        error.message.includes(
          "Organization has reached its maximum number of seats"
        )
      ) {
        // Redirect to waitlist page when there are no seats
        navigate("/waitlist");
        return;
      }
    },
    onSuccess: (data) => {
      authEventBus.emitEvent("auth:signup:completed", {
        invitation,
      });

      // submit callback
      onSuccess?.(data);
    },
  });
}

export default useGoogleSignupMutation;
